import * as THREE from 'three';
import type { Kit } from '../seasons/kit.ts';

/**
 * The Long Hunt's chapter 5 props, shared by the rock's places (engine/roca.ts) and the bastion's trophy
 * (engine/world.ts): the small carved wooden boar the Wolf kept in its bed, the trophy itself (huntTrophy), and the
 * clan's long boat. Each adds its pieces to a Kit at a placement `m`
 * (local units: a figure is about one tall). Pale parts go to `cold` when given (a second Kit lit cold from within, so
 * a warm light never turns them cream or pink).
 */

const box = new THREE.BoxGeometry(1, 1, 1);
const cyl = new THREE.CylinderGeometry(1, 1, 1, 10);
const cone = new THREE.ConeGeometry(1, 1, 8);
const rock = new THREE.DodecahedronGeometry(1, 0);
const sph = new THREE.SphereGeometry(1, 10, 7);

const L = (x: number, y: number, z: number, sx: number, sy: number, sz: number, rx = 0, ry = 0, rz = 0): THREE.Matrix4 =>
  new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz)), new THREE.Vector3(sx, sy, sz));

/** The wooden boar: a child's toy, carved and worn smooth (about 0.35 long); its tusks a paler wood. */
export const woodenBoar = (kit: Kit, m: THREE.Matrix4, cold?: Kit): void => {
  const W = '#8a5a36';
  const D = '#6b4428';
  kit.add(sph, W, m, L(0, 0.13, 0, 0.11, 0.085, 0.17));
  kit.add(sph, D, m, L(0, 0.17, 0.03, 0.07, 0.05, 0.12));
  // Its head, snout and the ridge of its back carved in.
  kit.add(sph, W, m, L(0, 0.14, 0.16, 0.07, 0.065, 0.075));
  kit.add(cyl, D, m, L(0, 0.13, 0.235, 0.032, 0.05, 0.032, Math.PI / 2));
  kit.add(box, D, m, L(0, 0.215, -0.01, 0.025, 0.03, 0.2));
  for (const k of [-1, 1]) {
    kit.add(cone, D, m, L(k * 0.045, 0.21, 0.15, 0.022, 0.05, 0.022, -0.3, 0, k * 0.4));
    (cold ?? kit).add(cone, '#d9dde2', m, L(k * 0.035, 0.11, 0.24, 0.012, 0.05, 0.012, -1.2, 0, k * 0.5));
  }
  for (const [x, z] of [
    [-0.06, 0.09],
    [0.06, 0.09],
    [-0.06, -0.09],
    [0.06, -0.09],
  ] as const)
    kit.add(cyl, D, m, L(x, 0.04, z, 0.025, 0.08, 0.025));
};

/**
 * The Long Hunt's trophy in the bastion (docs/sendas/barbaro.md §10), its front toward +z, about 2 wide and 2.4 tall
 * (local units; the bastion scales it to the Way's size): on a plinth of the black rock's stone, a grey wolf's skin
 * stretched on a drying frame of driftwood (a wolf of the clans', like Sigrun's pelt: of the Grey Wolf nothing is left),
 * its head over the top bar, the clan's red claw paint across it; before it, Sigrun's axe bitten into a great stump
 * (`axe`) or her diary at the foot of the stone with thirty-one strokes; at its right, the wooden boar, large, on a
 * flat stone. Pale parts go to `cold`; the wolf's skin and head to `fur` (its own cold-lit material: under the keep's
 * fire a plain grey turned pink). The boar stands front left, clear of where the hero idles on the right.
 */
export const huntTrophy = (kit: Kit, m: THREE.Matrix4, axe: boolean, cold: Kit, fur: Kit): void => {
  // The plinth: two courses of the rock's black stone with broken stones round its edge, the clan's red wool at the front.
  kit.add(box, '#2a2f34', m, L(0, 0.09, 0.05, 2.3, 0.18, 1.3));
  kit.add(box, '#394047', m, L(0, 0.2, 0.05, 2.14, 0.05, 1.16));
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + 0.3;
    kit.add(rock, i % 2 ? '#30363c' : '#3d444b', m, L(Math.sin(a) * 1.12, 0.1, 0.05 + Math.cos(a) * 0.62, 0.16 + (i % 3) * 0.04, 0.12, 0.16, i, i * 0.7, 0));
  }
  kit.add(box, '#9a2b24', m, L(0, 0.21, 0.63, 2.1, 0.05, 0.035));
  // Ash strewn before it, pale grey.
  kit.add(cyl, '#7f868e', m, L(0.05, 0.226, 0.32, 0.55, 0.008, 0.32));
  // The drying frame: two posts of driftwood leaning out a touch, the bars lashed with red thongs.
  const WOOD = '#4c3f34';
  for (const k of [-1, 1]) {
    kit.add(cyl, WOOD, m, L(k * 0.82, 1.27, -0.36, 0.055, 2.14, 0.055, 0, 0, -k * 0.04));
    kit.add(cone, WOOD, m, L(k * 0.86, 2.38, -0.36, 0.055, 0.1, 0.055));
  }
  kit.add(cyl, WOOD, m, L(0, 2.14, -0.36, 0.042, 1.86, 0.042, 0, 0, Math.PI / 2));
  kit.add(cyl, WOOD, m, L(0, 0.56, -0.36, 0.042, 1.78, 0.042, 0, 0, Math.PI / 2));
  for (const [x, y] of [[-0.82, 2.14], [0.82, 2.14], [-0.8, 0.56], [0.8, 0.56]] as const) kit.add(cyl, '#9a2b24', m, L(x, y, -0.36, 0.07, 0.09, 0.07));
  // The skin, stretched flat in the frame: the body, the dark saddle down its spine, the pale belly at its edges.
  const GREY = '#7d8289';
  const DARK = '#464a50';
  const z = -0.33;
  fur.add(sph, GREY, m, L(0, 1.33, z, 0.36, 0.62, 0.035));
  fur.add(box, DARK, m, L(0, 1.36, z + 0.02, 0.2, 0.98, 0.03));
  // Its legs out to the frame's corners, a pale paw tied at each with red thong; the tail down to the bottom bar.
  for (const k of [-1, 1]) {
    fur.add(box, GREY, m, L(k * 0.54, 1.93, z, 0.12, 0.56, 0.03, 0, 0, -k * 0.876));
    fur.add(box, GREY, m, L(k * 0.53, 0.77, z, 0.12, 0.54, 0.03, 0, 0, -k * 2.153));
    for (const y of [2.1, 0.6]) {
      fur.add(sph, '#a3a9b1', m, L(k * 0.76, y, z + 0.02, 0.07, 0.06, 0.04));
      kit.add(box, '#9a2b24', m, L(k * 0.79, y, z + 0.04, 0.03, 0.1, 0.02, 0, 0, 0.4));
    }
  }
  fur.add(cone, GREY, m, L(0, 0.72, z, 0.09, 0.34, 0.03, Math.PI));
  fur.add(cone, DARK, m, L(0, 0.58, z + 0.005, 0.05, 0.1, 0.03, Math.PI));
  // The clan's hunting paint across it: three red claw strokes, slantwise.
  for (let i = 0; i < 3; i++) kit.add(box, '#b2302a', m, L(-0.12 + i * 0.12, 1.25, z + 0.045, 0.05, 0.62, 0.012, 0, 0, 0.35));
  // The head over the top bar, looking down at whoever stands before it: skull, brow, ears up, the long muzzle.
  const hm = new THREE.Matrix4().multiplyMatrices(m, L(0, 1.98, -0.22, 1, 1, 1, 0.5));
  fur.add(sph, GREY, hm, L(0, 0, 0, 0.2, 0.17, 0.22));
  fur.add(sph, DARK, hm, L(0, 0.08, -0.02, 0.14, 0.08, 0.17));
  fur.add(cyl, GREY, hm, L(0, -0.03, 0.25, 0.07, 0.3, 0.11, Math.PI / 2));
  fur.add(box, '#a3a9b1', hm, L(0, -0.1, 0.22, 0.13, 0.05, 0.26));
  kit.add(sph, '#16171a', hm, L(0, -0.01, 0.41, 0.05, 0.045, 0.045));
  for (const k of [-1, 1]) {
    fur.add(cone, DARK, hm, L(k * 0.11, 0.17, -0.07, 0.07, 0.13, 0.05, -0.2, 0, -k * 0.35));
    kit.add(sph, '#16171a', hm, L(k * 0.09, 0.04, 0.15, 0.03, 0.025, 0.02));
    cold.add(cone, '#dfe4ea', hm, L(k * 0.045, -0.12, 0.34, 0.016, 0.07, 0.016, Math.PI));
  }
  if (axe) {
    // The great stump: its bark, its roots, its cut top with the rings; the axe bitten deep into it, its haft up.
    const S = new THREE.Matrix4().multiplyMatrices(m, L(0.1, 0.22, 0.2, 1, 1, 1, 0, 0.3));
    kit.add(cyl, '#3a2f27', S, L(0, 0.3, 0, 0.36, 0.6, 0.36));
    kit.add(cyl, '#6c5645', S, L(0, 0.605, 0, 0.335, 0.015, 0.335));
    for (const r of [0.24, 0.13]) kit.add(cyl, '#57453a', S, L(0, 0.615, 0, r, 0.008, r));
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2 + 0.4;
      kit.add(cone, '#3a2f27', S, L(Math.sin(a) * 0.38, 0.07, Math.cos(a) * 0.38, 0.09, 0.28, 0.09, Math.cos(a) * 1.25, 0, -Math.sin(a) * 1.25));
    }
    const tilt = -0.35;
    kit.add(cyl, '#5b4532', S, L(0.02, 1.12, -0.2, 0.04, 1.1, 0.04, tilt));
    for (let i = 0; i < 4; i++) kit.add(cyl, i % 2 ? '#b8322a' : '#7a2420', S, L(0.02, 1.42 + i * 0.08, -0.31 - i * 0.03, 0.05, 0.06, 0.05, tilt));
    cold.add(box, '#c5ccd4', S, L(0.02, 0.68, 0.02, 0.055, 0.28, 0.42, tilt));
    cold.add(box, '#e3e8ee', S, L(0.02, 0.6, 0.1, 0.035, 0.12, 0.48, tilt));
    kit.add(box, '#2f3338', S, L(0.02, 0.76, -0.06, 0.08, 0.14, 0.12, tilt));
  } else {
    // The standing stone with its thirty-one strokes (five rows of six and one), the diary on a flat stone at its foot.
    const S = new THREE.Matrix4().multiplyMatrices(m, L(0.1, 0.22, 0.1, 1, 1, 1, 0, 0.1));
    fur.add(rock, '#6a727b', S, L(0, 0.62, -0.05, 0.4, 0.68, 0.2, 0, 0.1, 0.05));
    for (let i = 0; i < 31; i++) {
      const row = Math.floor(i / 7);
      const col = i % 7;
      cold.add(box, '#e8edf2', S, L(-0.2 + col * 0.065, 1.0 - row * 0.15, 0.13 - Math.abs(col - 3) * 0.01, 0.014, 0.11, 0.02, 0, 0, col === 6 ? 0.9 : 0.05));
    }
    kit.add(rock, '#5b636c', S, L(0, 0.06, 0.32, 0.36, 0.09, 0.26));
    kit.add(box, '#3b2a22', S, L(0, 0.17, 0.32, 0.34, 0.05, 0.25, 0, 0.2));
    cold.add(box, '#e4e8ec', S, L(0, 0.205, 0.32, 0.32, 0.03, 0.23, 0, 0.2));
    kit.add(box, '#3b2a22', S, L(0, 0.225, 0.32, 0.34, 0.02, 0.25, 0, 0.2));
    kit.add(box, '#9a2b24', S, L(0, 0.235, 0.32, 0.35, 0.012, 0.025, 0, 0.2));
  }
  // The wooden boar, large on its flat stone at the front left, turned a little to the camera.
  kit.add(rock, '#4d555d', m, L(-0.64, 0.24, 0.34, 0.34, 0.05, 0.26));
  woodenBoar(kit, new THREE.Matrix4().multiplyMatrices(m, L(-0.64, 0.27, 0.34, 2.2, 2.2, 2.2, 0, 0.7)), cold);
};

/**
 * The clan's long boat (open, low, six oars, shields on its sides, a boar's head at the prow), its keel along +z; about
 * four long. `red`: Ivar's, its prow red. Rowers left to the caller.
 */
export const longBoat = (kit: Kit, m: THREE.Matrix4, red = false): void => {
  for (let i = 0; i < 9; i++) {
    const t = i / 8 - 0.5;
    const w = 0.6 * Math.cos(t * Math.PI * 0.92) + 0.04;
    const rise = Math.pow(Math.abs(t) * 2, 3) * 0.3;
    kit.add(box, '#3b3029', m, L(0, 0.1 + rise, t * 4.0, w * 1.3, 0.12, 0.5));
    for (const k of [-1, 1]) kit.add(box, i % 2 ? '#4b3d33' : '#54453a', m, L(k * w * 0.72, 0.32 + rise, t * 4.0, 0.07, 0.4, 0.52, 0, 0, -k * 0.32));
  }
  // The shields on the gunwales, red and grey by turns.
  for (let i = 0; i < 5; i++)
    for (const k of [-1, 1]) {
      const z = -1.2 + i * 0.6;
      const w = 0.6 * Math.cos((z / 4) * Math.PI * 0.92) + 0.04;
      kit.add(cyl, (i + (k > 0 ? 1 : 0)) % 2 ? '#7f2a22' : '#5d646c', m, L(k * (w * 0.78 + 0.02), 0.5, z, 0.2, 0.03, 0.2, 0, 0, Math.PI / 2));
      kit.add(cyl, '#2b2e33', m, L(k * (w * 0.78 + 0.04), 0.5, z, 0.05, 0.03, 0.05, 0, 0, Math.PI / 2));
    }
  // The oars, three a side, out and down.
  for (let i = 0; i < 3; i++) for (const k of [-1, 1]) kit.add(box, '#6a5846', m, L(k * 1.05, 0.28, -0.9 + i * 0.9, 1.5, 0.04, 0.06, 0, 0, k * 0.28));
  // The boar's head at the prow (red with Ivar).
  kit.add(rock, red ? '#b23a2a' : '#5d544c', m, L(0, 0.92, -2.25, 0.3, 0.32, 0.46));
  for (const k of [-1, 1]) kit.add(cone, '#d6dbe0', m, L(k * 0.14, 0.82, -2.55, 0.05, 0.22, 0.05, -1.3, 0, k * 0.4));
  kit.add(sph, '#ff4f3a', m, L(0, 0.98, -2.48, 0.05, 0.05, 0.05));
};

/** The small boat (Sten's): two benches, two oars. About 2.4 long, keel along +z. */
export const skiffBoat = (kit: Kit, m: THREE.Matrix4): void => {
  for (let i = 0; i < 7; i++) {
    const t = i / 6 - 0.5;
    const w = 0.42 * Math.cos(t * Math.PI * 0.9) + 0.04;
    const rise = Math.pow(Math.abs(t) * 2, 3) * 0.18;
    kit.add(box, '#3e322a', m, L(0, 0.08 + rise, t * 2.4, w * 1.3, 0.1, 0.4));
    for (const k of [-1, 1]) kit.add(box, '#56483c', m, L(k * w * 0.72, 0.24 + rise, t * 2.4, 0.06, 0.3, 0.42, 0, 0, -k * 0.3));
  }
  for (const z of [-0.3, 0.45]) kit.add(box, '#6a5846', m, L(0, 0.22, z, 0.6, 0.04, 0.12));
  for (const k of [-1, 1]) kit.add(box, '#6a5846', m, L(k * 0.75, 0.25, 0.05, 1.1, 0.035, 0.05, 0, 0.2 * k, k * 0.3));
};

/** A rower in a long boat (or in the clan's boats far off): a hooded figure, a horn at the bow's. */
export const rower = (kit: Kit, m: THREE.Matrix4, horn: boolean, seed: number): void => {
  const coat = ['#4b4f57', '#565049', '#3f454e', '#5b4c44'][seed % 4] as string;
  kit.add(cyl, coat, m, L(0, 0.32, 0, 0.16, 0.42, 0.14));
  kit.add(sph, '#b98d74', m, L(0, 0.62, 0.02, 0.09, 0.1, 0.09));
  kit.add(sph, coat, m, L(0, 0.66, -0.02, 0.1, 0.08, 0.1));
  if (horn) kit.add(cone, '#d2d6dc', m, L(0.12, 0.74, 0.08, 0.05, 0.28, 0.05, -1.1, 0, -0.5));
};
